/**
 * Client del xat ric de Miranda (`plans/miranda-mes-senzilla.md`, peça 4): el transport de
 * `useChat` contra `POST /agent/chat/:agentId/ui`, l'historial filtrat d'una sessió i les
 * accions de les tres targetes. Totes les crides passen pel client autenticat (`api`,
 * `authFetch`): mateix token i mateix 401 → refresc → un reintent que la resta de l'app.
 *
 * Els tipus de l'stream són una còpia dels del backend (`modules/agent/ui-stream.ts`). Quan la
 * peça de polítiques defineixi el contracte al backend, tot plegat ha d'anar a
 * `@guilleumes/types`.
 */
import { DefaultChatTransport, isTextUIPart, type UIMessage } from 'ai';

import type { SuspendedRun } from '@/hooks/usePendingWorkflows';
import type { MirandaFocusPayload } from '@/lib/miranda-focus';
import { SINGLE_MIRANDA_AGENT_ID } from '@/lib/miranda-agents';
import { api, authFetch } from '@/services/api';

/**
 * L'agent que activa el xat ric, a `/chat` i al giny lateral (`MirandaSheet`): la Miranda única.
 * La resta, el client de text d'avui. És el mateix id que el valor per defecte del selector
 * (`lib/miranda-agents.ts`).
 */
export const RICH_CHAT_AGENT_ID = SINGLE_MIRANDA_AGENT_ID;

/**
 * Quin xat pinta `/chat`. **Mana la conversa oberta**, digui el que digui el selector: el ric si és
 * de `miranda`, el de text si és d'un altre agent. Sense conversa oberta (o mentre no se sap de qui
 * és), mana el selector.
 *
 * Una conversa de `miranda` no la pot pintar mai el xat de text: aquell llegeix
 * `GET /agent/sessions/:id`, que torna les files tal com són a `chat_messages` (sortides de tool
 * senceres i el rastre de les targetes com a missatges d'usuari). I una d'un altre agent no s'ha
 * de seguir amb `miranda` (quedaria barrejada): amb `miranda` per defecte, entrar per l'adreça a
 * `/chat/<id>` d'una conversa vella ha de pintar el xat de text.
 */
export function usesRichChat(selectedAgentId: string, openSessionAgentId?: string | null): boolean {
  if (openSessionAgentId) return openSessionAgentId === RICH_CHAT_AGENT_ID;
  return selectedAgentId === RICH_CHAT_AGENT_ID;
}

/**
 * A `/chat`, triar `nextAgentId` al selector amb una conversa oberta n'obre una de nova? Sí quan es
 * passa del xat de text al ric o al revés: una conversa de `miranda` no es pot seguir amb el xat de
 * text, i una d'un altre agent seguida amb `miranda` quedava barrejada (i a la llista, amb el nom
 * de l'altre agent). Entre dos agents de text, com sempre: la conversa continua.
 */
export function agentChangeStartsNewChat(
  nextAgentId: string,
  richChatOpen: boolean,
  openSessionId: string | null,
): boolean {
  return !!openSessionId && usesRichChat(nextAgentId) !== richChatOpen;
}

/**
 * El prefix amb què el backend desa el rastre d'una acció de targeta a `chat_messages` (rol
 * `user`, perquè el model el llegeixi). **Còpia** de `TRAIL_PREFIX` d'`agent-proposals.service.ts`:
 * el backend no pot importar res en execució d'un paquet compartit, i el test de paritat
 * (`miranda-rich-chat.test.ts`) llegeix el fitxer del backend i falla si divergeixen.
 */
export const CARD_ACTION_TRAIL_PREFIX = '[Acció a la targeta del xat, no és un missatge escrit]';

/**
 * El text d'un rastre de targeta sense el prefix, o `null` si el text no és cap rastre. Xarxa de
 * seguretat del navegador: l'historial ja arriba amb el rastre com a part `data-card-action`, però
 * un text que comenci pel prefix no es pinta mai com una bombolla, vingui d'on vingui. El prefix
 * només compta al principi (s'hi toleren espais i salts de línia al davant).
 */
export function cardActionTrailText(text: unknown): string | null {
  if (typeof text !== 'string') return null;
  const trimmed = text.trimStart();
  if (!trimmed.startsWith(CARD_ACTION_TRAIL_PREFIX)) return null;
  return trimmed.slice(CARD_ACTION_TRAIL_PREFIX.length).trim();
}

// ── Stream ──────────────────────────────────────────────────────────────────────────────────

/** El punter d'un workflow suspès (part `data-workflow`). Mai porta el `suspendPayload`. */
export interface WorkflowPointer {
  runId: string;
  workflowId: string | null;
  status: 'suspended';
  toolName: string;
  resumePath: string;
}

export interface MirandaUIMetadata {
  sessionId?: string;
  usage?: { tokensIn: number; tokensOut: number; total: number };
  timedOut?: boolean;
}

/**
 * El rastre d'una acció feta a una targeta (part `data-card-action`): aprovar, rebutjar o desfer.
 * El backend el desa perquè Miranda ho sàpiga al torn següent; no l'ha escrit ningú al xat.
 */
export interface CardAction {
  text: string;
}

export type MirandaUIData = { workflow: WorkflowPointer; 'card-action': CardAction };
export type MirandaUIMessage = UIMessage<MirandaUIMetadata, MirandaUIData>;

/** Fitxer adjunt tal com el vol `ChatRequestDto` (`files`). */
export interface RichChatFile {
  dataUri: string;
  name: string;
  mimeType: string;
}

type FetchLike = (path: string, init: {
  method?: string;
  headers?: Record<string, string>;
  body?: BodyInit | null;
  signal?: AbortSignal | null;
}) => Promise<Response>;

export interface MirandaTransportOptions {
  agentId: string;
  /** La sessió en el moment d'enviar (pot haver arribat a mig primer torn). */
  getSessionId: () => string | null;
  /** La sessió que diu el servidor a `X-Session-Id`, a cada resposta. */
  onSessionId: (sessionId: string) => void;
  /**
   * El focus (`ChatFocusDto`) en el moment d'enviar, no en crear el transport: amb la fitxa oberta
   * es pot canviar de reserva mentre s'escriu. Sense getter, o si torna `undefined` (cap fitxa, o
   * el xip descartat), el cos no porta `focus`. `/chat` no el passa, com el seu client de text.
   */
  getFocus?: () => MirandaFocusPayload | undefined;
  /** Per als tests. Per defecte, `authFetch`. */
  fetchImpl?: FetchLike;
}

/**
 * El transport de `useChat`. Dues coses que el transport per defecte no fa:
 *
 * - **El cos és `ChatRequestDto`**, no el de l'AI SDK: `{ message, sessionId?, files?, focus? }`. El
 *   backend munta l'historial des de `chat_messages` i el `ValidationPipe` rebutja qualsevol
 *   camp de més (`messages`, `trigger`, `id`).
 * - **El `fetch` és `authFetch`**, i de la resposta se'n llegeix `X-Session-Id`.
 *
 * El senyal d'aturar (`stop()`) viatja tal com el dona l'AI SDK: avortar-lo talla la connexió.
 */
export function createMirandaTransport({
  agentId,
  getSessionId,
  onSessionId,
  getFocus,
  fetchImpl = authFetch as FetchLike,
}: MirandaTransportOptions) {
  return new DefaultChatTransport<MirandaUIMessage>({
    api: `/agent/chat/${agentId}/ui`,
    fetch: async (input, init) => {
      const headers: Record<string, string> = {};
      new Headers(init?.headers).forEach((value, key) => {
        // `user-agent` l'afegeix l'AI SDK; un navegador no deixa posar-lo i no fa cap falta.
        if (key.toLowerCase() !== 'user-agent') headers[key] = value;
      });
      const response = await fetchImpl(String(input), {
        method: init?.method ?? 'POST',
        headers,
        body: init?.body,
        signal: init?.signal,
      });
      const sessionId = response.headers.get('X-Session-Id');
      if (sessionId) onSessionId(sessionId);
      return response;
    },
    prepareSendMessagesRequest: ({ messages, body }) => {
      const last = messages[messages.length - 1];
      const message = (last?.parts ?? [])
        .filter(isTextUIPart)
        .map((part) => part.text)
        .join('');
      const files = (body as { files?: RichChatFile[] } | undefined)?.files;
      return {
        body: {
          message,
          sessionId: getSessionId() ?? undefined,
          files: files && files.length > 0 ? files : undefined,
          // Del client només ids; el bloc de context el munta el backend amb el rol de qui pregunta.
          focus: getFocus?.() ?? undefined,
        },
      };
    },
  });
}

// ── Historial ───────────────────────────────────────────────────────────────────────────────

export interface RichChatSession {
  id: string;
  agentId: string;
  title: string | null;
  messages: MirandaUIMessage[];
}

// ── Propostes (contracte de la peça de polítiques) ──────────────────────────────────────────

export type WriteKind = 'executed' | 'proposal' | 'refused';

export interface ProposalChange {
  target: { reservationId?: string; label?: string };
  field: string;
  before: unknown;
  after: unknown;
}

/** El resultat d'una tool d'escriptura, tal com arriba a la sortida de la tool. */
export interface WriteToolResult {
  kind: WriteKind;
  actionType?: string;
  summary?: string;
  changes?: ProposalChange[];
  /** Només amb `kind: 'proposal'`. */
  proposalId?: string;
  status?: string;
  /** Només amb `kind: 'executed'`, i opcional. */
  undoId?: string;
  /** Amb `kind: 'executed'`: fet, però cal mirar alguna cosa. Un text tancat del backend. */
  attention?: string;
}

export type ProposalStatus = 'approved' | 'rejected' | 'undone' | 'pending' | 'failed';

/** Una reserva que ha quedat per fer, amb les dates on havia de quedar si el canvi en movia. */
export interface ProposalPartialPending {
  reservation: string;
  checkIn?: string;
  checkOut?: string;
}

/** Una proposta aplicada a mitges: números de reserva i dates `AAAA-MM-DD`, res més. */
export interface ProposalPartial {
  done: string[];
  failed: string | null;
  pending: ProposalPartialPending[];
  /** Desades al PMS però sense la confirmació de Beds24 (no es reintenta sol); només hi és si n'hi ha. */
  beds24Pending?: ProposalPartialPending[];
}

/** El que responen els quatre endpoints de `/agent/proposals/:id`. */
export interface ProposalResolution {
  id: string;
  status: ProposalStatus;
  result?: unknown;
  error?: string;
  /** Amb `approved`: aplicada, però cal mirar alguna cosa. Un text tancat del backend. */
  attention?: string;
  /** Amb `failed`: aplicada en part. */
  partial?: ProposalPartial;
}

// ── Workflows ───────────────────────────────────────────────────────────────────────────────

export function isInvoiceWorkflowName(workflowName: string): boolean {
  return workflowName === 'process-invoice' || workflowName === 'process-invoice-pdf';
}

/** El que respon `POST /mastra/resume*`. */
export interface WorkflowResumeResult {
  runId?: string;
  status?: string;
  result?: { success?: boolean; message?: string } & Record<string, unknown>;
  error?: string;
}

/** L'únic workflow que es decideix des del xat. La resta, a `/admin/workflows`. */
export const CHAT_RESUMABLE_WORKFLOW = 'cancel-reservation';

/**
 * Es pot aprovar o rebutjar des del xat? Només la cancel·lació d'una reserva, que no demana cap
 * tria. Una factura (compte, tipus de despesa, forma de pagament, total) i una conciliació
 * (moviments) es decideixen a `/admin/workflows`, on hi ha el formulari; un workflow que el xat
 * no coneix, també. És una llista d'admesos: un workflow nou no s'hi cola per defecte.
 */
export function canResumeWorkflowFromChat(workflowName: string): boolean {
  return workflowName === CHAT_RESUMABLE_WORKFLOW;
}

/**
 * El cos de `POST /mastra/resume/:runId` des del xat, **només per a la cancel·lació d'una
 * reserva**: `{ approved, workflowName }`, que és el que envia `WorkflowApprovalsPage.handleResume`
 * per a una cancel·lació, tret d'`approvedBy` (el backend el treu de qui fa la petició, MIR-7, i
 * ignora el del cos).
 *
 * Per a qualsevol altre workflow **llança**: el xat no pot construir l'aprovació d'una factura
 * (crearia una despesa a Cuentica amb un compte i una forma de pagament que ningú ha vist) ni la
 * d'una conciliació.
 */
export function buildWorkflowResumeRequest(
  run: Pick<SuspendedRun, 'runId' | 'workflowName'>,
  approved: boolean,
): { path: string; body: Record<string, unknown> } {
  if (!canResumeWorkflowFromChat(run.workflowName)) {
    throw new Error(
      `El workflow «${run.workflowName || 'desconegut'}» no es decideix des del xat: obre'l a Aprovacions.`,
    );
  }
  return {
    path: `/mastra/resume/${run.runId}`,
    body: { approved, workflowName: run.workflowName },
  };
}

export const mirandaRichApi = {
  /** L'historial d'una sessió com a `UIMessage[]`, amb la sortida de cada tool filtrada. */
  getSessionUi: async (sessionId: string): Promise<RichChatSession> => {
    const response = await api.get(`/agent/sessions/${sessionId}/ui`);
    return response.data;
  },

  /** Els workflows que esperen aprovació, amb el detall (`GET /mastra/pending`). */
  getPendingRuns: async (): Promise<SuspendedRun[]> => {
    const response = await api.get('/mastra/pending');
    const data = response.data;
    return Array.isArray(data) ? data : (data?.value ?? []);
  },

  /** Només la cancel·lació d'una reserva: per a la resta, `buildWorkflowResumeRequest` llança. */
  resumeWorkflow: async (
    run: Pick<SuspendedRun, 'runId' | 'workflowName'>,
    approved: boolean,
  ): Promise<WorkflowResumeResult> => {
    const { path, body } = buildWorkflowResumeRequest(run, approved);
    const response = await api.post(path, body);
    return response.data;
  },

  /** L'estat d'una proposta (`proposalId`) o d'una acció executada (`undoId`): és la mateixa fila. */
  getProposal: async (id: string): Promise<ProposalResolution> => {
    const response = await api.get(`/agent/proposals/${encodeURIComponent(id)}`);
    return response.data;
  },

  approveProposal: async (id: string): Promise<ProposalResolution> => {
    const response = await api.post(`/agent/proposals/${encodeURIComponent(id)}/approve`);
    return response.data;
  },

  rejectProposal: async (id: string): Promise<ProposalResolution> => {
    const response = await api.post(`/agent/proposals/${encodeURIComponent(id)}/reject`);
    return response.data;
  },

  undoProposal: async (id: string): Promise<ProposalResolution> => {
    const response = await api.post(`/agent/proposals/${encodeURIComponent(id)}/undo`);
    return response.data;
  },
};
